import { cloneDeep, get, omit } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { isHotKeyPress } from '@/utils/event-kit';
import { TextMeasurer } from '@/utils/text-kit';
import { LayerType, MaterialIdsType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { type FormatBrushData } from '../types';
import { extractFormatBrushDataFromLayer, pushHistory, updateLayer } from '../utils';
import { type IArtText } from './art-text';
import { type IShapeText } from './shape-text';

export interface IFormatBrush {
    /**
     * 提取格式刷数据
     * @param layerID - 图层 id
     * @returns
     */
    extractFormatBrushData(layerID: string): FormatBrushData | undefined;
    /**
     * 应用格式刷
     * @param layerID - 图层 id
     * @returns
     */
    applyFormatBrush(layerID: string): Promise<ILayerProps | void>;
}

export class FormatBrush implements IFormatBrush {
    private formatBrushData: FormatBrushData | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
        private artText: IArtText,
    ) {}

    extractFormatBrushData(layerID: string) {
        const layer = this.framework.getLayer(layerID, true);
        if (!layer || layer.layerType !== LayerType.TEXT_LAYER) return;

        this.formatBrushData = extractFormatBrushDataFromLayer(layer);

        return this.formatBrushData;
    }

    private calcLayout(layer: ILayerProps, options: { exchangeEdge: boolean }) {
        const { exchangeEdge } = options;

        let nextLayerWidth = layer.width;
        let nextLayerHeight = layer.height;

        if (exchangeEdge) {
            nextLayerWidth = layer.height;
            nextLayerHeight = layer.width;
        }

        const nextLayer: ILayerProps = {
            ...layer,
            width: nextLayerWidth,
            height: nextLayerHeight,
        };

        const textSize = TextMeasurer.measure(nextLayer, {
            keepConstantWidth: get(nextLayer, '__fixedDirectionSize', false),
            keepConstantHeight: false,
        });

        nextLayerWidth = textSize.width;
        nextLayerHeight = textSize.height;

        const nextLayerTop = layer.top + (nextLayer.height - nextLayerHeight) / 2;
        const nextLayerLeft = layer.left + (nextLayer.width - nextLayerWidth) / 2;

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
            top: nextLayerTop,
            left: nextLayerLeft,
        };
    }

    private updateFontFamilyMaterialIDs(layer: ILayerProps) {
        const prevMaterialIDs = layer.materialIds;
        const layerData = layer.data as ITextLayerProps;
        const { fontFamily, textContents = [] } = layerData;

        const layerFontFamilies = (() => {
            const ret: string[] = [fontFamily];
            if (textContents.length > 0) {
                textContents.forEach((textContent) => {
                    if (textContent.fontFamily) ret.push(textContent.fontFamily);
                });
            }
            return ret;
        })();

        const editorFontFamilies = this.framework.getFontList();
        const fontFamilyMaterialIDs: string[] = [];

        layerFontFamilies.forEach((item) => {
            const font = editorFontFamilies.find((font) => font['font_name'] === item);
            if (font && font['material_id']) {
                fontFamilyMaterialIDs.push(String(font['material_id']));
            }
        });

        const nextMaterialIDs = prevMaterialIDs ? cloneDeep(prevMaterialIDs) : {};
        nextMaterialIDs[MaterialIdsType.FONT_FAMILY] = fontFamilyMaterialIDs;

        const nextLayer: ILayerProps = {
            ...layer,
            materialIds: nextMaterialIDs,
        };

        return nextLayer;
    }

    async applyFormatBrush(layerID: string) {
        if (this.formatBrushData == null) return;

        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const { opacity } = this.formatBrushData;

        let materialIds = cloneDeep(this.formatBrushData.materialIds);
        let data = cloneDeep(this.formatBrushData.data);

        // 如果目标格式刷数据包含花字特效，且与源图层的花字特效不一致，则直接走应用花字特效逻辑
        if (data.advancedTextMaterial) {
            if (
                prevLayerData.advancedTextMaterial &&
                prevLayerData.advancedTextMaterial.id === data.advancedTextMaterial.id
            ) {
                data = omit(data, 'advancedTextMaterial');
                if (materialIds) materialIds = omit(materialIds, MaterialIdsType.ADVANCED_TEXT_MATERIAL);
            } else {
                const formatBrushData = cloneDeep(this.formatBrushData);

                this.formatBrushData = null;

                return this.artText.applyArtText(layerID, data.advancedTextMaterial, {
                    formatBrushData,
                });
            }
        }

        const nextLayerData: ITextLayerProps = { ...prevLayerData, ...data };

        let nextLayer: ILayerProps = {
            ...prevLayer,
            opacity,
            materialIds,
            data: nextLayerData,
        };

        // 需要校验真实使用的字体，来确认最终的素材 ID，防止前一步操作添加了多余的字体素材 ID
        // 比如源图层中有部分字体使用了 vip 字体，但是应用格式刷时并不会使用 textContents 中的 vip 字体
        // 但是源图层的 materialIds 中却有 vip 字体的素材 ID
        // 故需要重新计算字体素材 ID
        nextLayer = this.updateFontFamilyMaterialIDs(nextLayer);

        nextLayer.__fixedDirectionSize = false; // 默认重置宽高

        const fontFamily = nextLayerData.fontFamily;
        await this.framework.registerFont(nextLayer, fontFamily, true);

        const { shapeExtension } = nextLayerData;
        if (!shapeExtension) {
            const layout = this.calcLayout(nextLayer, {
                exchangeEdge: prevLayerData.writingMode !== data.writingMode,
            });

            nextLayer.width = layout.width;
            nextLayer.height = layout.height;
            nextLayer.top = layout.top;
            nextLayer.left = layout.left;
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }
        if (isHotKeyPress()) {
            this.framework.stage.addOrUpdate(nextLayer);
        } else {
            updateLayer(this.framework, nextLayer);
            this.formatBrushData = null;
        }
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].copyBrush);

        return nextLayer;
    }
}
